import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { VerificationBanner } from "@/components/VerificationBanner";
import { DemoBanner } from "@/components/DemoBanner";
import {
  Sparkles,
  MessageSquare,
  History,
  User,
  CreditCard,
  ShieldCheck,
  Users,
  Package,
  Activity,
  Mail,
  TrendingUp,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useState } from "react";

const studentNav = [
  { to: "/panel", label: "Genel Bakış", icon: Sparkles, end: true, testid: "nav-panel" },
  { to: "/panel/sohbet", label: "Sohbet", icon: MessageSquare, testid: "nav-chat" },
  { to: "/panel/gecmis", label: "Geçmiş", icon: History, testid: "nav-history" },
  { to: "/panel/profil", label: "Profil", icon: User, testid: "nav-profile" },
  { to: "/panel/abonelik", label: "Abonelik", icon: CreditCard, testid: "nav-subscription" },
];

const adminNav = [
  { to: "/admin", label: "Yönetim", icon: ShieldCheck, end: true, testid: "nav-admin" },
  { to: "/admin/kullanicilar", label: "Kullanıcılar", icon: Users, testid: "nav-admin-users" },
  { to: "/admin/abonelikler", label: "Abonelikler", icon: CreditCard, testid: "nav-admin-subs" },
  { to: "/admin/sayisal-paketler", label: "Sayısal Paketler", icon: Package, testid: "nav-admin-sayisal-packages" },
  { to: "/admin/sayisal-huni", label: "Tanılama Hunisi", icon: TrendingUp, testid: "nav-admin-sayisal-funnel" },
  { to: "/admin/ai-telemetri", label: "AI Telemetri", icon: Activity, testid: "nav-admin-ai-telemetry" },
  { to: "/admin/bekleme-listesi", label: "Bekleme Listesi", icon: Mail, testid: "nav-admin-waitlist" },
];

export function AppShell({ children, variant = "student" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [sidebar, setSidebar] = useState(false);
  const items = variant === "admin" ? adminNav : studentNav;

  const handleLogout = async () => {
    // Navigate to the public landing FIRST so ProtectedRoute doesn't intercept
    // the render cycle triggered by setUser(false) and redirect the user to
    // /giris?next=... — we want a clean bounce back to the marketing site.
    nav("/", { replace: true });
    await logout();
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 z-30 h-screen w-64 shrink-0 bg-white border-r border-zinc-200 flex flex-col transform transition-transform ${
          sidebar ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        data-testid="app-sidebar"
      >
        <div className="h-16 px-5 flex items-center justify-between border-b border-zinc-100">
          <Link to={variant === "admin" ? "/admin" : "/panel"} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">SuperZeka</span>
          </Link>
          <button
            className="lg:hidden p-1 text-zinc-500"
            onClick={() => setSidebar(false)}
            aria-label="Kapat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setSidebar(false)}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-zinc-100 text-zinc-900"
                    : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-zinc-100">
          {user && user.role === "admin" && variant === "student" && (
            <Link
              to="/admin"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
              data-testid="link-admin-panel"
            >
              <ShieldCheck className="w-4 h-4" />
              Yönetim Paneli
            </Link>
          )}
          {user && user.role === "admin" && variant === "admin" && (
            <Link
              to="/panel"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
            >
              <Sparkles className="w-4 h-4" />
              Öğrenci Paneli
            </Link>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
          >
            <LogOut className="w-4 h-4" />
            Çıkış Yap
          </button>
          <div className="mt-3 px-3 py-2 rounded-xl bg-zinc-50 border border-zinc-100">
            <div className="text-xs text-zinc-500">Giriş yapıldı</div>
            <div className="text-sm font-medium text-zinc-900 truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          </div>
        </div>
      </aside>

      {sidebar && (
        <div
          className="fixed inset-0 z-20 bg-zinc-900/40 lg:hidden"
          onClick={() => setSidebar(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-10 bg-white border-b border-zinc-200 h-14 px-4 flex items-center justify-between">
          <button
            onClick={() => setSidebar(true)}
            className="p-2 -ml-2 text-zinc-700"
            aria-label="Menü"
            data-testid="sidebar-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#1E3A8A] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold tracking-tight">SuperZeka</span>
          </div>
          <div className="w-8" />
        </header>
        <main className="flex-1 min-w-0">
          <DemoBanner />
          {variant === "student" && <VerificationBanner />}
          {children}
        </main>
      </div>
    </div>
  );
}
